/**
 * Collection Tabs Carousel
 * Handles tab switching and carousel navigation for collection product displays
 */
class CollectionTabsCarousel extends HTMLElement {
  constructor() {
    super();

    this.currentPanel = null;
    this.currentCarousel = null;
    this.scrollHandler = null;
    this.resizeObserver = null;
    /** @type {Record<string, number>} Scroll position per panel (panelId -> scrollLeft) */
    this.scrollPositions = {};
  }

  connectedCallback() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    // Get elements after connection
    this.tabButtons = this.querySelectorAll("[data-tab-trigger]");
    this.panels = this.querySelectorAll("[data-collection-panel]");
    this.navContainer = this.querySelector("[data-carousel-nav]");
    this.ctaLinks = this.querySelectorAll("[data-collection-cta]");
    this.defaultCtaUrl = this.dataset.defaultCtaUrl || "";

    if (!this.navContainer) {
      console.error("Navigation container not found");
      return;
    }

    // Set up tab click handlers
    this.tabButtons.forEach((button) => {
      button.addEventListener("click", (e) => this.handleTabClick(e));

      // Keyboard navigation
      button.addEventListener("keydown", (e) => this.handleTabKeydown(e));
    });

    // Initialize first tab
    this.initializeFirstTab();
  }

  initializeFirstTab() {
    // Wait for custom elements to be defined
    if (customElements.get("slideshow-component")) {
      this.setupFirstTab();
    } else {
      customElements.whenDefined("slideshow-component").then(() => {
        this.setupFirstTab();
      });
    }
  }

  setupFirstTab() {
    const firstTab = this.tabButtons[0];
    if (firstTab) {
      const panelId = firstTab.dataset.tab;
      this.switchTab(panelId);
    }
  }

  handleTabClick(e) {
    const button = e.currentTarget;
    const panelId = button.dataset.tab;

    this.switchTab(panelId);
  }

  handleTabKeydown(e) {
    const currentIndex = Array.from(this.tabButtons).indexOf(e.target);
    let nextIndex;

    switch (e.key) {
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        nextIndex =
          currentIndex > 0 ? currentIndex - 1 : this.tabButtons.length - 1;
        this.tabButtons[nextIndex].focus();
        this.tabButtons[nextIndex].click();
        break;
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        nextIndex =
          currentIndex < this.tabButtons.length - 1 ? currentIndex + 1 : 0;
        this.tabButtons[nextIndex].focus();
        this.tabButtons[nextIndex].click();
        break;
      case "Home":
        e.preventDefault();
        this.tabButtons[0].focus();
        this.tabButtons[0].click();
        break;
      case "End":
        e.preventDefault();
        this.tabButtons[this.tabButtons.length - 1].focus();
        this.tabButtons[this.tabButtons.length - 1].click();
        break;
    }
  }

  switchTab(panelId) {
    // Save scroll position of current panel before switching away
    if (this.currentPanel) {
      const scroller = this.currentPanel.querySelector("slideshow-slides");
      if (scroller instanceof HTMLElement) {
        const currentPanelId = this.currentPanel.dataset.collectionPanel;
        this.scrollPositions[currentPanelId] = scroller.scrollLeft;
      }
    }

    // Update tab buttons
    this.tabButtons.forEach((button) => {
      const isActive = button.dataset.tab === panelId;
      button.setAttribute("aria-selected", isActive);

      if (isActive) {
        button.classList.add("border-coal-700", "text-coal-700", "font-bold");
        button.classList.remove("border-transparent", "text-coal-500");
      } else {
        button.classList.remove(
          "border-coal-700",
          "text-coal-700",
          "font-bold",
        );
        button.classList.add("border-transparent", "text-coal-500");
      }
    });
    this.updateCollectionCta(panelId);

    // Update panels
    this.panels.forEach((panel) => {
      const isActive = panel.dataset.collectionPanel === panelId;

      if (isActive) {
        const scroller = panel.querySelector("slideshow-slides");

        // Disable scroll-snap BEFORE unhiding so the browser doesn't snap to a random slide
        if (scroller instanceof HTMLElement) {
          scroller.style.scrollSnapType = "none";
          scroller.style.scrollBehavior = "auto";
        }

        panel.removeAttribute("hidden");
        this.currentPanel = panel;

        requestAnimationFrame(() => {
          const carousel = panel.querySelector("slideshow-component");

          if (carousel) {
            this.currentCarousel = carousel;
            this.waitForCarouselReady(carousel, panelId, scroller);
          } else {
            // Re-enable scroll-snap even if carousel not found
            if (scroller instanceof HTMLElement) {
              scroller.style.scrollSnapType = "";
              scroller.style.scrollBehavior = "";
            }
            console.error("No slideshow-component found in panel!");
          }
        });
      } else {
        panel.setAttribute("hidden", "");
      }
    });
  }

  updateCollectionCta(panelId) {
    if (!this.ctaLinks.length) return;

    const activeButton = Array.from(this.tabButtons).find(
      (button) => button.dataset.tab === panelId,
    );

    const ctaUrl = activeButton?.dataset.ctaUrl || this.defaultCtaUrl;

    if (!ctaUrl) return;

    this.ctaLinks.forEach((link) => {
      link.href = ctaUrl;
    });
  }

  waitForCarouselReady(carousel, panelId, scroller) {
    const onReady = () => {
      this.updateCarouselNav(carousel);
      this.restoreOrResetScrollPosition(carousel, panelId, scroller);
    };

    // Check if carousel has the necessary methods (next/previous are directly on the component)
    if (
      typeof carousel.next === "function" &&
      typeof carousel.previous === "function"
    ) {
      onReady();
    } else {
      let attempts = 0;
      // Wait for carousel to initialize
      const checkReady = setInterval(() => {
        attempts++;
        if (
          typeof carousel.next === "function" &&
          typeof carousel.previous === "function"
        ) {
          clearInterval(checkReady);
          onReady();
        } else if (attempts > 40) {
          // After 2 seconds (40 * 50ms), try to create nav anyway
          console.warn("Carousel methods not found, creating nav anyway");
          clearInterval(checkReady);
          onReady();
        }
      }, 50);
    }
  }

  /**
   * Set scroll position while scroll-snap is disabled, then re-enable snap.
   * Scroll-snap was already disabled before the panel was unhidden to prevent
   * the browser from snapping to a random slide.
   */
  restoreOrResetScrollPosition(carousel, panelId, scroller) {
    if (!(scroller instanceof HTMLElement)) return;

    const savedPosition = this.scrollPositions[panelId];
    const targetLeft = typeof savedPosition === "number" ? savedPosition : 0;

    // Set position while snap is still disabled — no fighting with the snap engine
    scroller.scrollLeft = targetLeft;

    // Also use the slideshow's own API to sync its internal state
    if (typeof carousel.select === "function") {
      const targetIndex = targetLeft === 0 ? 0 : carousel.current || 0;
      carousel.select(targetIndex, undefined, { animate: false });
    }

    // Re-enable scroll-snap after a frame so the browser doesn't override our position
    requestAnimationFrame(() => {
      scroller.style.scrollSnapType = "";
      scroller.style.scrollBehavior = "";
    });
  }

  updateCarouselNav(carousel) {
    if (!this.navContainer) {
      console.error("Nav container not found!");
      return;
    }

    if (!carousel) {
      console.error("Carousel not found!");
      return;
    }

    // Clear existing nav
    this.navContainer.innerHTML = "";

    // Create previous button
    const prevButton = this.createNavButton("prev", carousel);
    this.navContainer.appendChild(prevButton);

    // Create next button
    const nextButton = this.createNavButton("next", carousel);
    this.navContainer.appendChild(nextButton);

    // Update button states
    this.updateNavButtonStates(carousel);

    // Listen for carousel changes to update button states
    const slideshowSlides = carousel.querySelector("slideshow-slides");
    if (slideshowSlides) {
      // Remove any existing scroll listener to avoid duplicates
      if (this.scrollHandler) {
        slideshowSlides.removeEventListener("scroll", this.scrollHandler);
      }

      // Create and store scroll handler
      this.scrollHandler = () => {
        this.updateNavButtonStates(this.currentCarousel);
      };

      slideshowSlides.addEventListener("scroll", this.scrollHandler, {
        passive: true,
      });

      // ResizeObserver: update button states when viewport/slider size changes
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      this.resizeObserver = new ResizeObserver(() => {
        this.updateNavButtonStates(this.currentCarousel);
      });
      this.resizeObserver.observe(slideshowSlides);

      // Deferred update for initial load - layout may not be ready yet
      requestAnimationFrame(() => {
        this.updateNavButtonStates(this.currentCarousel);
      });
      setTimeout(() => this.updateNavButtonStates(this.currentCarousel), 0);
    }
  }

  createNavButton(direction, carousel) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `carousel-nav-button flex items-center justify-center   text-coal-700 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed`;
    button.setAttribute(
      "aria-label",
      direction === "prev" ? "Previous slide" : "Next slide",
    );
    button.dataset.direction = direction;

    // SVG arrow icon
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", "w-8 h-8");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("aria-hidden", "true");

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    if (direction === "prev") {
      path.setAttribute("d", "M15 18l-6-6 6-6");
    } else {
      path.setAttribute("d", "M9 18l6-6-6-6");
    }

    svg.appendChild(path);
    button.appendChild(svg);

    // Add click handler - use arrow function to keep 'this' context
    button.addEventListener("click", (e) => {
      e.preventDefault();

      // Get the current carousel (might have changed if user switched tabs)
      const activeCarousel = this.currentCarousel;

      if (!activeCarousel) {
        console.warn("Carousel not available");
        return;
      }

      if (
        typeof activeCarousel.next !== "function" ||
        typeof activeCarousel.previous !== "function"
      ) {
        console.warn("Carousel navigation methods not available");
        return;
      }

      try {
        if (direction === "prev") {
          activeCarousel.previous();
        } else {
          activeCarousel.next();
        }

        // Update button states after navigation
        setTimeout(() => this.updateNavButtonStates(activeCarousel), 100);
      } catch (error) {
        console.error("Error navigating carousel:", error);
      }
    });

    return button;
  }

  updateNavButtonStates(carousel) {
    if (!this.navContainer || !carousel) return;

    const prevButton = this.navContainer.querySelector(
      '[data-direction="prev"]',
    );
    const nextButton = this.navContainer.querySelector(
      '[data-direction="next"]',
    );

    if (!prevButton || !nextButton) return;

    // Check if carousel is scrollable (all slides fit in view = no scroll needed)
    const scroller = carousel.querySelector("slideshow-slides");
    if (scroller instanceof HTMLElement) {
      const isScrollable = scroller.scrollWidth > scroller.clientWidth + 1;
      if (!isScrollable) {
        prevButton.disabled = true;
        nextButton.disabled = true;
        return;
      }
    }

    // Use slideshow's nextIndex for accurate end detection (handles visibleSlides async)
    const slides = carousel.slides || [];
    const currentIndex = carousel.current !== undefined ? carousel.current : 0;
    const nextIndex =
      typeof carousel.nextIndex === "number"
        ? carousel.nextIndex
        : currentIndex + 1;

    // Disable prev button at start
    prevButton.disabled = currentIndex <= 0;

    // Disable next button at end (matches slideshow-component internal logic)
    nextButton.disabled = slides.length === 0 || nextIndex >= slides.length;
  }
}

// Register the custom element
if (!customElements.get("collection-tabs-carousel")) {
  customElements.define("collection-tabs-carousel", CollectionTabsCarousel);
}
